import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Drawer,
  Form,
  Input,
  Table,
  Button,
  Space,
  message,
  Row,
  Col,
  Select,
  Divider,
  Typography,
  Card,
  Pagination,
  Tag,
  Alert,
  Switch,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, SearchOutlined, ShopOutlined, BarcodeOutlined, PlusOutlined } from '@ant-design/icons';
import type { Store } from '../../../types/master-data/store';
import { StoreFormat } from '../../../types/master-data/store';
import * as storeService from '../../../services/master-data/store/storeService';

const { Text } = Typography;

// Store format options (aligned with Spec StoreFormat enum)
const STORE_FORMAT_OPTIONS = [
  { label: 'Go Fresh', value: StoreFormat.FRESH },
  { label: 'Super', value: StoreFormat.SUPERMARKET },
  { label: 'Hyper', value: StoreFormat.HYPER },
];

// Store format labels for display
const STORE_FORMAT_LABELS: Record<string, string> = {
  [StoreFormat.FRESH]: 'Go Fresh',
  [StoreFormat.SUPERMARKET]: 'Super',
  [StoreFormat.HYPER]: 'Hyper',
};

interface BatchAddStoreModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (selectedStores: Store[], applyStoreTypes: string[], excludeStores: string[]) => void;
  existingStoreCodes: string[];
  initialApplyStoreTypes?: string[];
  initialExcludeStores?: string[];
  initialSelectedStoreCodes?: string[]; // 已选中的门店编码，用于显示打勾状态
}

const BatchAddStoreModal: React.FC<BatchAddStoreModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  existingStoreCodes,
  initialApplyStoreTypes = [],
  initialExcludeStores = [],
  initialSelectedStoreCodes = [],
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [allStores, setAllStores] = useState<Store[]>([]);
  
  // Store type mode states
  const [selectedStoreTypes, setSelectedStoreTypes] = useState<string[]>([]);
  const [excludeStoreCodes, setExcludeStoreCodes] = useState<string[]>([]);
  
  // Mode toggle switches
  const [byStoreTypeEnabled, setByStoreTypeEnabled] = useState<boolean>(false);
  const [byStoreCodeEnabled, setByStoreCodeEnabled] = useState<boolean>(false);

  // Store code input mode states
  const [storeCodeInput, setStoreCodeInput] = useState<string>('');
  const [storeCodeSelectedStores, setStoreCodeSelectedStores] = useState<Store[]>([]);
  const [isStoreCodeInputFocused, setIsStoreCodeInputFocused] = useState<boolean>(false);

  // 手动选择的门店编码（用于表格行选择）
  const [manualSelectedKeys, setManualSelectedKeys] = useState<string[]>([]);
  
  // 排除门店批量输入状态
  const [excludeStoreInput, setExcludeStoreInput] = useState<string>('');
  const [isExcludeInputFocused, setIsExcludeInputFocused] = useState<boolean>(false);
  const [excludeStorePage, setExcludeStorePage] = useState<number>(1);
  const [excludeStorePageSize, setExcludeStorePageSize] = useState<number>(10);

  // Load all stores when modal opens
  useEffect(() => {
    if (visible) {
      loadAllStores();
      // Set initial values only when opening (not when initial values change)
      setSelectedStoreTypes(initialApplyStoreTypes);
      setExcludeStoreCodes(initialExcludeStores);
      setManualSelectedKeys(initialSelectedStoreCodes);
      setStoreCodeSelectedStores([]);
      // 将已选门店编码填入输入框，方便编辑时查看
      setStoreCodeInput(initialSelectedStoreCodes.length > 0 ? initialSelectedStoreCodes.join(', ') : '');
      // Enable switch if initial values exist
      setByStoreTypeEnabled(initialApplyStoreTypes.length > 0);
      setByStoreCodeEnabled(initialSelectedStoreCodes.length > 0);
      form.setFieldsValue({
        applyStoreTypes: initialApplyStoreTypes,
        excludeStores: initialExcludeStores,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // 当 allStores 加载完成后，将初始已选门店编码匹配为完整门店对象显示在已选列表中
  useEffect(() => {
    if (visible && allStores.length > 0 && initialSelectedStoreCodes.length > 0 && storeCodeSelectedStores.length === 0) {
      const initialCodeSet = new Set(initialSelectedStoreCodes);
      const matchedStores = allStores.filter(store => initialCodeSet.has(store.storeCode));
      if (matchedStores.length > 0) {
        setStoreCodeSelectedStores(matchedStores);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, allStores, initialSelectedStoreCodes]);

  // Load all stores with pagination loop
  const loadAllStores = async () => {
    setLoading(true);
    try {
      const pageSize = 1000;
      let currentPage = 1;
      let allLoadedStores: Store[] = [];

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const response = await storeService.pageStores({ currentPage, pageSize });

        if (!response.success || !response.data) {
          message.error(response.errorMessage || t('common.message.operationFailed'));
          return;
        }

        const { tableData = [], paging } = response.data;
        allLoadedStores = allLoadedStores.concat(tableData);

        if (allLoadedStores.length >= paging.totalCount) {
          break;
        }
        currentPage++;
      }

      setAllStores(allLoadedStores);
    } catch (error) {
      message.error(t('common.message.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Parse input codes from storeCode field
  const parseInputCodes = (input: string): string[] => {
    if (!input) return [];
    return input
      .split(/[\n,\s]+/)
      .map((code) => code.trim().toUpperCase())
      .filter((code) => code.length > 0);
  };

  // Get stores by store types (excluding specified stores)
  const storesByType = useMemo(() => {
    if (selectedStoreTypes.length === 0) return [];
    
    return allStores.filter((store) => {
      const typeMatch = selectedStoreTypes.includes(store.storeFormat || '');
      const notExcluded = !excludeStoreCodes.includes(store.storeCode);
      const notExisting = !existingStoreCodes.includes(store.storeCode);
      
      return typeMatch && notExcluded && notExisting;
    });
  }, [allStores, selectedStoreTypes, excludeStoreCodes, existingStoreCodes]);

  // 计算当前选中的门店编码（合并自动选择和手动选择）
  const selectedRowKeys = useMemo(() => {
    const codes: string[] = [...manualSelectedKeys];
    // 添加通过门店类型选择的门店编码
    storesByType.forEach((store) => {
      if (!codes.includes(store.storeCode)) {
        codes.push(store.storeCode);
      }
    });
    // storeCodeSelectedStores 仅作为表格数据源，选中状态完全由 manualSelectedKeys 控制
    return codes;
  }, [storesByType, manualSelectedKeys]);

  // Handle store type change
  const handleStoreTypeChange = (types: string[]) => {
    setSelectedStoreTypes(types);
    form.setFieldValue('applyStoreTypes', types);
  };

  // Handle exclude stores change (for Select component)
  const handleExcludeStoresChange = (codes: string[]) => {
    setExcludeStoreCodes(codes);
    form.setFieldValue('excludeStores', codes);
  };

  // Parse exclude store input to codes
  const parseExcludeStoreInput = (input: string): string[] => {
    if (!input) return [];
    return input
      .split(/[\n,\s]+/)
      .map((code) => code.trim().toUpperCase())
      .filter((code) => code.length > 0);
  };

  // Handle exclude stores input change (for TextArea input)
  const handleExcludeStoresInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const input = e.target.value;
    const codes = parseExcludeStoreInput(input);
    setExcludeStoreCodes(codes);
    form.setFieldValue('excludeStores', input);
  };

  // Handle store code search
  const handleStoreCodeSearch = () => {
    const codes = parseInputCodes(storeCodeInput);
    if (codes.length === 0) {
      message.warning(t('wave.waveRule.form.batchAddStore.validation.inputRequired'));
      return;
    }

    const foundStores = allStores.filter(
      (store) => store.storeCode && codes.includes(store.storeCode.toUpperCase()) && !existingStoreCodes.includes(store.storeCode)
    );

    if (foundStores.length === 0) {
      message.info(t('wave.waveRule.form.batchAddStore.message.noStoresFound'));
      setStoreCodeSelectedStores([]);
      // 同步清空 manualSelectedKeys，避免初始化残留的门店编码还出现在 selectedRowKeys 中
      setManualSelectedKeys([]);
    } else {
      setStoreCodeSelectedStores(foundStores);
      // 同步重置 manualSelectedKeys 为搜索结果，避免初始化时残留的门店编码导致 selectedRowKeys 包含多余门店
      setManualSelectedKeys(foundStores.map((store) => store.storeCode));
      message.success(
        t('wave.waveRule.form.batchAddStore.message.searchSuccess', { count: foundStores.length })
      );
    }
  };

  // Handle confirm
  const handleConfirm = useCallback(() => {
    // 获取所有选中的门店编码
    const allSelectedCodes = selectedRowKeys;

    if (allSelectedCodes.length === 0 && !(byStoreTypeEnabled && selectedStoreTypes.length > 0)) {
      message.warning(t('wave.waveRule.form.batchAddStore.validation.selectAtLeastOne'));
      return;
    }

    // 根据编码找到对应的门店对象
    const selectedStores = allStores.filter((store) => allSelectedCodes.includes(store.storeCode));

    onConfirm(selectedStores, selectedStoreTypes, excludeStoreCodes);
    handleReset();
  }, [selectedRowKeys, allStores, selectedStoreTypes, excludeStoreCodes, onConfirm, t, byStoreTypeEnabled]);

  // Handle reset
  const handleReset = useCallback(() => {
    form.resetFields();
    setSelectedStoreTypes([]);
    setExcludeStoreCodes([]);
    setStoreCodeInput('');
    setStoreCodeSelectedStores([]);
    setIsStoreCodeInputFocused(false);
    setManualSelectedKeys([]);
    setByStoreTypeEnabled(false);
    setByStoreCodeEnabled(false);
    setExcludeStoreInput('');
    setIsExcludeInputFocused(false);
    setExcludeStorePage(1);
  }, [form]);

  // Handle cancel
  const handleCancelModal = useCallback(() => {
    handleReset();
    onCancel();
  }, [handleReset, onCancel]);

  // 获取所有可选的门店列表（始终显示所有门店，让用户可以看到选中状态）
  const allSelectableStores = useMemo(() => {
    return allStores.filter((store) =>
      !existingStoreCodes.includes(store.storeCode) && !excludeStoreCodes.includes(store.storeCode)
    );
  }, [allStores, existingStoreCodes, excludeStoreCodes]);

  // Table columns for selected stores (By Store Code mode)
  const selectedStoreColumns = [
    {
      title: t('wave.waveRule.form.batchAddStore.table.no'),
      key: 'no',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: t('wave.waveRule.form.batchAddStore.table.storeCode'),
      dataIndex: 'storeCode',
      key: 'storeCode',
      width: 120,
    },
    {
      title: t('wave.waveRule.form.batchAddStore.table.storeName'),
      dataIndex: 'storeName',
      key: 'storeName',
      width: 200,
    },
    {
      title: t('wave.waveRule.form.batchAddStore.fields.storeType'),
      dataIndex: 'storeFormat',
      key: 'storeFormat',
      width: 120,
      render: (storeFormat: StoreFormat) => (
        <Tag color={storeFormat === StoreFormat.FRESH ? 'green' : storeFormat === StoreFormat.SUPERMARKET ? 'blue' : 'orange'}>
          {STORE_FORMAT_LABELS[storeFormat] || storeFormat}
        </Tag>
      ),
    },
    {
      title: t('wave.waveRule.form.batchAddStore.table.actions'),
      key: 'actions',
      width: 80,
      render: (_: any, record: Store) => (
        <Tooltip title={t('wave.waveRule.form.batchAddStore.actions.removeExclude')}>
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveSelectedStore(record.storeCode)}
          />
        </Tooltip>
      ),
    },
  ];

  // Get stores available for exclusion (based on selected store types)
  const storesAvailableForExclusion = useMemo(() => {
    if (selectedStoreTypes.length === 0) return [];
    return allStores.filter((store) => selectedStoreTypes.includes(store.storeFormat || ''));
  }, [allStores, selectedStoreTypes]);
  
  // 获取已排除的门店列表（包含门店详情）
  const excludedStoresList = useMemo(() => {
    if (excludeStoreCodes.length === 0) return [];
    return allStores.filter((store) => excludeStoreCodes.includes(store.storeCode));
  }, [allStores, excludeStoreCodes]);
  
  // 分页后的已排除门店列表
  const paginatedExcludedStores = useMemo(() => {
    const start = (excludeStorePage - 1) * excludeStorePageSize;
    const end = start + excludeStorePageSize;
    return excludedStoresList.slice(start, end);
  }, [excludedStoresList, excludeStorePage, excludeStorePageSize]);

  // 处理批量输入排除门店
  const handleBatchExcludeStores = () => {
    const codes = parseExcludeStoreInput(excludeStoreInput);
    if (codes.length === 0) {
      message.warning(t('wave.waveRule.form.batchAddStore.validation.inputRequired'));
      return;
    }
    
    // 验证输入的门店编码是否属于选中的门店类型
    const validCodes: string[] = [];
    const invalidCodes: string[] = [];
    
    codes.forEach((code) => {
      const store = allStores.find((s) => s.storeCode.toUpperCase() === code.toUpperCase());
      if (store && selectedStoreTypes.includes(store.storeFormat || '')) {
        validCodes.push(store.storeCode);
      } else {
        invalidCodes.push(code);
      }
    });
    
    if (invalidCodes.length > 0) {
      message.warning(
        t('wave.waveRule.form.batchAddStore.message.invalidStoreCodes', { 
          codes: invalidCodes.join(', ') 
        })
      );
    }
    
    if (validCodes.length > 0) {
      const newExcludeStoreCodes = [...new Set([...excludeStoreCodes, ...validCodes])];
      setExcludeStoreCodes(newExcludeStoreCodes);
      form.setFieldValue('excludeStores', newExcludeStoreCodes);
      setExcludeStoreInput('');
      setIsExcludeInputFocused(false);
      message.success(
        t('wave.waveRule.form.batchAddStore.message.excludeSuccess', { count: validCodes.length })
      );
    }
  };
  
  // 删除单个排除门店
  const handleRemoveExcludedStore = (storeCode: string) => {
    const newExcludeStoreCodes = excludeStoreCodes.filter((code) => code !== storeCode);
    setExcludeStoreCodes(newExcludeStoreCodes);
    form.setFieldValue('excludeStores', newExcludeStoreCodes);
  };
  
  // 删除单个选中的门店
  // By Store Type 模式：将门店加入排除列表（storesByType 会自动重新计算并排除该门店）
  // By Store Code 模式：从 storeCodeSelectedStores 和 manualSelectedKeys 中直接移除
  const handleRemoveSelectedStore = (storeCode: string) => {
    if (byStoreTypeEnabled) {
      // By Store Type 模式：加入排除列表，storesByType useMemo 会自动过滤
      const newExcludeStoreCodes = [...new Set([...excludeStoreCodes, storeCode])];
      setExcludeStoreCodes(newExcludeStoreCodes);
      form.setFieldValue('excludeStores', newExcludeStoreCodes);
    } else {
      // By Store Code 模式：直接从已选列表中移除
      setStoreCodeSelectedStores((prev) => prev.filter((store) => store.storeCode !== storeCode));
      setManualSelectedKeys((prev) => prev.filter((code) => code !== storeCode));
    }
  };
  
  // 重置排除门店
  const handleResetExcludedStores = () => {
    setExcludeStoreCodes([]);
    form.setFieldValue('excludeStores', []);
    setExcludeStorePage(1);
    setExcludeStoreInput('');
    setIsExcludeInputFocused(false);
  };

  // Total selected count
  const totalSelectedCount = useMemo(() => {
    return selectedRowKeys.length;
  }, [selectedRowKeys]);

  return (
    <Drawer
      title={t('wave.waveRule.form.batchAddStore.title')}
      open={visible}
      onClose={handleCancelModal}
      width="50vw"
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        {/* 提示信息 */}
        <Alert
          message={t('wave.waveRule.form.batchAddStore.hint')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* 模式开关（单选互斥） */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={24} align="middle">
            <Col>
              <Space align="center" size={8}>
                <ShopOutlined style={{ fontSize: 16, color: byStoreTypeEnabled ? '#1890ff' : '#bfbfbf' }} />
                <span style={{ fontWeight: 500 }}>{t('wave.waveRule.form.batchAddStore.mode.byStoreType')}</span>
                <Switch
                  checked={byStoreTypeEnabled}
                  onChange={(checked) => {
                    setByStoreTypeEnabled(checked);
                    if (checked) {
                      // 互斥：关闭 By Store Code 并清空其状态
                      setByStoreCodeEnabled(false);
                      setStoreCodeInput('');
                      setStoreCodeSelectedStores([]);
                      setIsStoreCodeInputFocused(false);
                    } else {
                      setSelectedStoreTypes([]);
                      setExcludeStoreCodes([]);
                      form.setFieldsValue({ applyStoreTypes: [], excludeStores: [] });
                    }
                  }}
                  size="small"
                />
              </Space>
            </Col>
            <Col>
              <Divider type="vertical" />
            </Col>
            <Col>
              <Space align="center" size={8}>
                <BarcodeOutlined style={{ fontSize: 16, color: byStoreCodeEnabled ? '#1890ff' : '#bfbfbf' }} />
                <span style={{ fontWeight: 500 }}>{t('wave.waveRule.form.batchAddStore.mode.byStoreCode')}</span>
                <Switch
                  checked={byStoreCodeEnabled}
                  onChange={(checked) => {
                    setByStoreCodeEnabled(checked);
                    if (checked) {
                      // 互斥：关闭 By Store Type 并清空其状态
                      setByStoreTypeEnabled(false);
                      setSelectedStoreTypes([]);
                      setExcludeStoreCodes([]);
                      form.setFieldsValue({ applyStoreTypes: [], excludeStores: [] });
                    } else {
                      setStoreCodeInput('');
                      setStoreCodeSelectedStores([]);
                      setIsStoreCodeInputFocused(false);
                    }
                  }}
                  size="small"
                />
              </Space>
            </Col>
          </Row>
        </Card>

        {/* 方式一：按门店类型选择 */}
        {byStoreTypeEnabled && (
        <Card 
          size="small" 
          style={{ marginBottom: 16 }}
          title={
            <Space>
              <Text strong>{t('wave.waveRule.form.batchAddStore.mode.byStoreType')}</Text>
              {storesByType.length > 0 && (
                <Tag color="blue">{storesByType.length} {t('wave.waveRule.form.batchAddStore.storesSelected')}</Tag>
              )}
            </Space>
          }
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="applyStoreTypes"
                label={t('wave.waveRule.form.fields.applyStoreTypes')}
              >
                <Select
                  mode="multiple"
                  placeholder={t('wave.waveRule.form.placeholder.applyStoreTypes')}
                  options={STORE_FORMAT_OPTIONS}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleStoreTypeChange}
                />
              </Form.Item>
            </Col>
          </Row>
          
          {/* 排除门店批量输入 */}
          {selectedStoreTypes.length > 0 && (
            <>
              <Divider style={{ margin: '12px 0' }} />
              <div style={{ marginBottom: 16 }}>
                <Row gutter={16}>
                  <Col span={18}>
                    {isExcludeInputFocused ? (
                      <Input.TextArea
                        rows={3}
                        placeholder={t('wave.waveRule.form.batchAddStore.placeholder.excludeStoreMulti')}
                        value={excludeStoreInput}
                        onChange={(e) => setExcludeStoreInput(e.target.value)}
                        onBlur={() => {
                          if (!excludeStoreInput) {
                            setIsExcludeInputFocused(false);
                          }
                        }}
                        allowClear
                        autoFocus
                      />
                    ) : (
                      <Input
                        placeholder={t('wave.waveRule.form.batchAddStore.placeholder.excludeStoreMulti')}
                        value={excludeStoreInput}
                        onChange={(e) => setExcludeStoreInput(e.target.value)}
                        onFocus={() => setIsExcludeInputFocused(true)}
                        allowClear
                      />
                    )}
                  </Col>
                  <Col span={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={handleBatchExcludeStores} 
                        block
                      >
                        {t('wave.waveRule.form.batchAddStore.actions.addExclude')}
                      </Button>
                      {excludeStoreCodes.length > 0 && (
                        <Button 
                          danger 
                          onClick={handleResetExcludedStores} 
                          block
                        >
                          {t('wave.waveRule.form.batchAddStore.actions.reset')}
                        </Button>
                      )}
                    </Space>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </Card>
        )}
        
        {/* 已排除门店列表 - 选择门店类型后始终显示 */}
        {byStoreTypeEnabled && selectedStoreTypes.length > 0 && (
        <Card 
          size="small"
          style={{ marginBottom: 16 }}
          title={
            <Space>
              <Text strong>{t('wave.waveRule.form.batchAddStore.excludedStoresList')}</Text>
              {excludedStoresList.length > 0 && (
                <Tag color="orange">{excludeStoreCodes.length} {t('wave.waveRule.form.batchAddStore.storesExcluded')}</Tag>
              )}
            </Space>
          }
        >
          {excludedStoresList.length === 0 ? (
            <div style={{ padding: '24px 0', textAlign: 'center', color: '#999' }}>
              {t('wave.waveRule.form.batchAddStore.emptyText')}
            </div>
          ) : (
            <Table
              dataSource={paginatedExcludedStores}
              rowKey="code"
              size="small"
              pagination={{
                current: excludeStorePage,
                pageSize: excludeStorePageSize,
                total: excludedStoresList.length,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => t('common.pagination.total', { total }),
                onChange: (page, pageSize) => {
                  setExcludeStorePage(page);
                  setExcludeStorePageSize(pageSize);
                },
              }}
              columns={[
                {
                  title: t('wave.waveRule.form.batchAddStore.table.no'),
                  key: 'no',
                  width: 60,
                  render: (_: any, __: any, index: number) => {
                    const start = (excludeStorePage - 1) * excludeStorePageSize;
                    return start + index + 1;
                  },
                },
                {
                  title: t('wave.waveRule.form.batchAddStore.table.storeCode'),
                  dataIndex: 'storeCode',
                  key: 'storeCode',
                  width: 120,
                },
                {
                  title: t('wave.waveRule.form.batchAddStore.table.storeName'),
                  dataIndex: 'storeName',
                  key: 'storeName',
                  ellipsis: true,
                },
                {
                  title: t('wave.waveRule.form.batchAddStore.fields.storeType'),
                  dataIndex: 'storeFormat',
                  key: 'storeFormat',
                  width: 120,
                  render: (storeFormat: StoreFormat) => (
                    <Tag color={storeFormat === StoreFormat.FRESH ? 'green' : storeFormat === StoreFormat.SUPERMARKET ? 'blue' : 'orange'}>
                      {STORE_FORMAT_LABELS[storeFormat] || storeFormat}
                    </Tag>
                  ),
                },
                {
                  title: t('wave.waveRule.form.batchAddStore.table.actions'),
                  key: 'actions',
                  width: 80,
                  render: (_: any, record: Store) => (
                    <Tooltip title={t('wave.waveRule.form.batchAddStore.actions.removeExclude')}>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveExcludedStore(record.storeCode)}
                      />
                    </Tooltip>
                  ),
                },
              ]}
            />
          )}
        </Card>
        )}



        {/* 方式二：按门店编码选择 */}
        {byStoreCodeEnabled && (
        <Card 
          size="small"
          style={{ marginBottom: 16 }}
          title={
            <Space>
              <Text strong>{t('wave.waveRule.form.batchAddStore.mode.byStoreCode')}</Text>
              {storeCodeSelectedStores.length > 0 && (
                <Tag color="green">{storeCodeSelectedStores.length} {t('wave.waveRule.form.batchAddStore.storesSelected')}</Tag>
              )}
            </Space>
          }
        >
          <Row gutter={16}>
            <Col span={18}>
              <Form.Item
                label={t('wave.waveRule.form.batchAddStore.fields.storeCodeMulti')}
                extra={isStoreCodeInputFocused ? t('wave.waveRule.form.batchAddStore.fields.batchInputHint') : undefined}
                style={{ marginBottom: 0 }}
              >
                {isStoreCodeInputFocused ? (
                  <Input.TextArea
                    rows={3}
                    placeholder={t('wave.waveRule.form.batchAddStore.placeholder.storeCodeMulti')}
                    value={storeCodeInput}
                    onChange={(e) => setStoreCodeInput(e.target.value)}
                    onBlur={() => {
                      if (!storeCodeInput) {
                        setIsStoreCodeInputFocused(false);
                      }
                    }}
                    allowClear
                    autoFocus
                  />
                ) : (
                  <Input
                    placeholder={t('wave.waveRule.form.batchAddStore.placeholder.storeCodeMulti')}
                    value={storeCodeInput}
                    onChange={(e) => setStoreCodeInput(e.target.value)}
                    onFocus={() => setIsStoreCodeInputFocused(true)}
                    allowClear
                  />
                )}
              </Form.Item>
            </Col>
            <Col span={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button type="primary" onClick={handleStoreCodeSearch} loading={loading} block>
                  {t('common.actions.search')}
                </Button>
                <Button onClick={() => { setStoreCodeInput(''); setStoreCodeSelectedStores([]); setIsStoreCodeInputFocused(false); }} block>
                  {t('common.actions.reset')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
        )}
      </Form>

      {/* 门店列表 - 仅在 By Store Code 模式下显示 */}
      {byStoreCodeEnabled && (
      <Card
        size="small"
        title={
          <Space>
            <Text strong>{t('wave.waveRule.form.batchAddStore.selectedStoresList')}</Text>
            {storeCodeSelectedStores.length > 0 && (
              <Tag color="blue">{storeCodeSelectedStores.length} {t('wave.waveRule.form.batchAddStore.storesSelected')}</Tag>
            )}
          </Space>
        }
        style={{ marginTop: 16, marginBottom: 16 }}
      >
        <Table
          rowSelection={{
            selectedRowKeys: manualSelectedKeys,
            onChange: (newSelectedRowKeys) => {
              setManualSelectedKeys(newSelectedRowKeys as string[]);
            },
          }}
          columns={selectedStoreColumns}
          dataSource={storeCodeSelectedStores}
          rowKey="storeCode"
          pagination={{ pageSize: 10, size: 'small' }}
          size="small"
          scroll={{ y: 300 }}
        />
      </Card>
      )}

      {/* Footer Buttons */}
      <div style={{ marginTop: 24, textAlign: 'right', borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
        <Space>
          <Text type="secondary">
            {t('wave.waveRule.form.batchAddStore.selectedCount', { count: totalSelectedCount })}
          </Text>
          <Button onClick={handleCancelModal}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            disabled={totalSelectedCount === 0 && !(byStoreTypeEnabled && selectedStoreTypes.length > 0)}
          >
            {t('common.actions.confirm')}
          </Button>
        </Space>
      </div>
    </Drawer>
  );
};

export default BatchAddStoreModal;
